import { useIsMobile } from '@qse/utils'
import { useEffect, useRef, useState } from 'react'

/**
 * A highly stable intersection observer that prevents flickering on mobile
 * Uses custom Intersection Observer with large margins and aggressive throttling
 */
export const useStableInView = (
  ref: React.RefObject<HTMLElement>,
  _options?: {
    amount?: number | 'some' | 'all'
    once?: boolean
    debounceMs?: number
  },
) => {
  const isMobile = useIsMobile()
  const [isInView, setIsInView] = useState(true)
  const timeoutRef = useRef<NodeJS.Timeout>()
  const lastStateChangeRef = useRef<number>(Date.now())
  const lastValueRef = useRef<boolean>(true)
  const rafRef = useRef<number>()

  useEffect(() => {
    const element = ref.current

    if (!element) return

    // On mobile, use a very forgiving threshold with large margins
    // This prevents flickering during momentum scrolling
    const observerOptions: IntersectionObserverInit = isMobile
      ? {
          // Large root margin means the element needs to be way out of view to trigger
          rootMargin: '-150px 0px -150px 0px',
          // Lower threshold - element only needs to be slightly visible
          threshold: [0, 0.1, 0.5, 1.0],
        }
      : {
          rootMargin: '0px',
          threshold: [0, 1.0],
        }

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }

      rafRef.current = requestAnimationFrame(() => {
        const entry = entries[0]
        const newIsInView = entry.isIntersecting && entry.intersectionRatio >= 0.9

        // On mobile, add moderate throttling to prevent rapid changes while maintaining responsiveness
        if (isMobile) {
          const now = Date.now()
          const timeSinceLastChange = now - lastStateChangeRef.current
          const minChangeInterval = 300 // Balanced: 300ms between changes

          // Only update if the value actually changed AND enough time has passed
          if (newIsInView !== lastValueRef.current && timeSinceLastChange >= minChangeInterval) {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current)
            }

            // Minimal delay before committing the change for smooth handoff
            timeoutRef.current = setTimeout(() => {
              setIsInView(newIsInView)
              lastValueRef.current = newIsInView
              lastStateChangeRef.current = Date.now()
            }, 100)
          }
        } else {
          // Desktop: update immediately
          if (newIsInView !== lastValueRef.current) {
            setIsInView(newIsInView)
            lastValueRef.current = newIsInView
          }
        }
      })
    }

    const observer = new IntersectionObserver(handleIntersection, observerOptions)

    observer.observe(element)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      observer.disconnect()
    }
  }, [ref, isMobile])

  return isInView
}
